// What the testing engine has done with a test, addressed by the id both surfaces share.
//
// A test's life after planning belongs to the engine, not to the basis item leaning on it: one test
// can carry several controls, so no single item can own how far it has got. The item's own status
// still moves — a concluded test makes it obtained — but that move is worked out where the scenario
// is resolved, from what is recorded here.
import {useSyncExternalStore} from "react";
import {Outcome, Scenario} from "./model";
import {testIdFor} from "./testLink";

/** A test that has been dispatched to the engine and has not concluded. */
export type TestProgress = {
  /** Items examined so far, out of the test's sample size. */
  tested: number;
  exceptions: number;
};

/** What a finished test found. Enough to turn the item that planned it into an obtained one. */
export type TestResult = {itemsTested: number; exceptions: number; outcome: Outcome; conclusion: string};

// Replaced rather than mutated on write, so each map is a snapshot React can compare.
let progress: ReadonlyMap<string, TestProgress> = new Map();
let results: ReadonlyMap<string, TestResult> = new Map();
const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const emit = () => {
  for (const listener of listeners) listener();
};

/**
 * Where a started test is shown to have got to. Fieldwork happens in the testing module and there is
 * no engine behind the prototype, so starting one lands it part-way through. Derived from the id
 * rather than drawn, so a walkthrough shows the same figures on every run.
 */
const openingPosition = (testId: string, sampleSize: number): TestProgress => {
  const hash = [...testId].reduce((acc, ch) => (acc * 31 + ch.charCodeAt(0)) % 9973, 7);
  // A single-item test has no part-way point — the item is either examined or it is not, and a
  // freshly started one is not. Anything sampled lands somewhere inside its sample.
  const tested =
    sampleSize <= 1 ? 0 : Math.min(sampleSize, Math.max(1, Math.round((sampleSize * (30 + (hash % 45))) / 100)));
  return {tested, exceptions: tested > 0 && hash % 4 === 0 ? 1 : 0};
};

/**
 * The conclusion a finished test writes for itself. A first draft in the engine's own words, which
 * is what the auditor edits — the prototype has nobody to author one per test.
 */
const draftConclusion = (itemsTested: number, exceptions: number): string =>
  exceptions === 0
    ? `All ${itemsTested} items tested met every criterion, with no exceptions noted.`
    : `${itemsTested} items were tested. ${exceptions} did not meet every criterion and ${
        exceptions === 1 ? "has" : "have"
      } been raised as ${exceptions === 1 ? "an exception" : "exceptions"} for evaluation.`;

/**
 * What a test found, from what it saw. The outcome follows from the exceptions rather than being
 * chosen: a clean run operated effectively, anything else operated with exceptions. Whether that
 * still supports reliance is the control's conclusion to make, not the test's.
 */
const resultOf = (sampleSize: number, exceptions: number): TestResult => ({
  itemsTested: sampleSize,
  exceptions,
  outcome: exceptions === 0 ? "operated_effectively" : "operated_with_exceptions",
  conclusion: draftConclusion(sampleSize, exceptions),
});

/** Every test-shaped item a scenario has planned, with the id the engine knows it by. */
const plannedTests = (scenario: Scenario): {testId: string; sampleSize: number}[] =>
  scenario.oe.flatMap(({control, scope, basis}) =>
    basis.flatMap(item =>
      item.status === "planned" && item.evidence.kind === "test" && item.evidence.parameters
        ? [{testId: testIdFor(scenario.id, control, scope, item.id), sampleSize: item.evidence.parameters.sampleSize}]
        : [],
    ),
  );

export const startTest = (testId: string, sampleSize: number) => {
  progress = new Map(progress).set(testId, openingPosition(testId, sampleSize));
  emit();
};

/** Finish a test on whatever it has found so far. */
export const concludeTest = (testId: string, sampleSize: number) => {
  results = new Map(results).set(testId, resultOf(sampleSize, progress.get(testId)?.exceptions ?? 0));
  progress = new Map([...progress].filter(([id]) => id !== testId));
  emit();
};

/**
 * Move every test in the scenario on a step, in one go — the walkthrough control. Applied as a
 * single write so a scenario with several basis items advances as one, rather than redrawing per
 * test on the way through.
 */
export const startAllTests = (scenario: Scenario) => {
  const next = new Map(progress);
  for (const {testId, sampleSize} of plannedTests(scenario)) {
    if (!next.has(testId) && !results.has(testId)) next.set(testId, openingPosition(testId, sampleSize));
  }
  progress = next;
  emit();
};

export const concludeAllTests = (scenario: Scenario) => {
  const nextResults = new Map(results);
  const remaining = new Map(progress);
  for (const {testId, sampleSize} of plannedTests(scenario)) {
    const running = remaining.get(testId);
    if (!running) continue;
    nextResults.set(testId, resultOf(sampleSize, running.exceptions));
    remaining.delete(testId);
  }
  results = nextResults;
  progress = remaining;
  emit();
};

/**
 * Load the engine with the tests a scenario says are already running, and forget everything from the
 * last one. Switching scenario switches worlds: a test started or concluded by hand does not follow.
 */
export const seedScenario = (scenario: Scenario) => {
  const seeded = new Map<string, TestProgress>();
  for (const assessment of scenario.oe) {
    for (const [itemId, state] of Object.entries(assessment.underway ?? {})) {
      seeded.set(testIdFor(scenario.id, assessment.control, assessment.scope, itemId), state);
    }
  }
  progress = seeded;
  results = new Map();
  emit();
};

/** The engine's whole state, for the resolver that folds it into the active scenario. */
export const useEngineState = (): {
  progress: ReadonlyMap<string, TestProgress>;
  results: ReadonlyMap<string, TestResult>;
} => ({
  progress: useSyncExternalStore(subscribe, () => progress),
  results: useSyncExternalStore(subscribe, () => results),
});
